import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ErrorState, Loading } from "../../components/States";
import { PillView, SortTh, Toolbar, matchesSearch, useSorted } from "../../components/ui";
import { toast } from "../../components/Toast";
import { money } from "../../lib/format";
import { useCreatorBase, type CBooking, type CDeal, type CPeriod } from "./data";
import {
  activeDealIds, creatorOccurrences, creatorRules, csvAmount, dealStatusPill, flatTaxNote, isDirect, modelLabel, monthDeadline, monthDone, monthNeedsInvoice, monthPill,
  platformLabel, talentStateContext, toCsv, viewTalentInvoice, type MonthContext, type Occurrence, type TalentInvoiceView,
} from "./model";
import { DemoNote, downloadText, useCreatorContext, useCreatorVisibility } from "./ui";
import { dateOf, monthOf, useT } from "./sprache";
import { showsBudget } from "./sicht";

/** Deine Deals (12_app_creator/meine-deals.html) mit Archiv und Monatstabelle aus dem bisherigen Portal (TA-030, TA-034, TA-035). */

type Filter = "aktiv" | "erledigt" | "alle";
type SortKey = "monat" | "kooperation" | "stand" | "honorar" | "anteil" | "frist";

interface Card {
  key: string;
  deal: CDeal;
  period: CPeriod | null;
  title: string;
  month: string | null;
  done: boolean;
}

export function Deals() {
  const { scope, today, lang } = useCreatorContext();
  const t = useT();
  const navigate = useNavigate();
  const base = useCreatorBase(scope);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("aktiv");
  const b = base.data;
  const fmt = (d: string | null | undefined) => dateOf(d ?? null, lang);
  const visibility = useCreatorVisibility();
  const budgetVisible = showsBudget(visibility);

  const data = useMemo(() => {
    if (!b) return null;
    // Dieselbe Rechnungs-Sicht wie unter „Rechnungen“, damit ein Monat dort und hier denselben Stand zeigt (Befund 12)
    const rules = creatorRules(b.defaults, b.org);
    const ctx = talentStateContext(today, rules, b.org.currency);
    const views = new Map(b.invoices.map((r) => viewTalentInvoice(r, ctx, b.org.timezone, lang, rules)).filter((v): v is TalentInvoiceView => !!v).map((v) => [v.row.id, v]));
    const monthCtx: MonthContext = { today, views, selfBilling: !!b.talent.self_billing_agreed_on, ...(b.statusLabels ? { statusLabels: b.statusLabels } : {}) };
    const occ = new Map<string, Occurrence>();
    for (const o of creatorOccurrences({ today, talent: b.talent, deals: b.deals, periods: b.periods, lineItems: b.lineItems, invoices: b.invoices, bookings: [], rates: b.rates, agencyInvoices: b.agencyInvoices })) {
      if (o.periodId) occ.set(o.periodId, o);
    }
    const periodsByDeal = new Map<string, CPeriod[]>();
    for (const p of b.periods) periodsByDeal.set(p.deal_id, [...(periodsByDeal.get(p.deal_id) ?? []), p]);
    const cards: Card[] = [];
    const brand = (d: CDeal) => b.brands.get(d.brand_id)?.name ?? (lang === "en" ? "Brand" : "Marke");
    for (const d of [...b.deals].sort((x, y) => (y.start_month ?? "").localeCompare(x.start_month ?? ""))) {
      const periods = (periodsByDeal.get(d.id) ?? []).sort((x, y) => x.month.localeCompare(y.month));
      if (!periods.length) {
        cards.push({ key: d.id, deal: d, period: null, title: `${brand(d)} · ${d.title}`, month: d.start_month?.slice(0, 7) ?? null, done: ["done", "cancelled"].includes(d.status) });
        continue;
      }
      let first = true;
      for (const p of periods) {
        const done = monthDone(p, d, b.invoices, today);
        const month = p.month.slice(0, 7);
        // Wie im Design: erste Karte „Marke · Titel“, weitere Monate derselben Kooperation „Marke · Monat“
        cards.push({ key: p.id, deal: d, period: p, title: first || periods.length === 1 ? `${brand(d)} · ${d.title}` : `${brand(d)} · ${monthOf(month, lang, false)}`, month, done });
        if (!done) first = false;
      }
    }
    const active = activeDealIds(b.deals, b.periods, b.invoices, today);
    return { occ, periodsByDeal, cards, brand, monthCtx, active };
  }, [b, today, lang]);

  const rows = useMemo(() => {
    if (!b || !data) return [];
    return b.periods.map((p) => {
      const d = b.deals.find((x) => x.id === p.deal_id)!;
      const o = data.occ.get(p.id);
      const inv = b.invoices.filter((i) => i.period_id === p.id && !["withdrawn", "discarded", "cancelled"].includes(i.status));
      const due = monthDeadline(d, p, data.periodsByDeal.get(d.id)?.length ?? 1, b.deliverables);
      return { p, d, due, brand: data.brand(d), share: o?.shareCents ?? null, pill: monthPill(p, d, b.invoices, lang, data.monthCtx), invoice: inv.map((i) => i.number ?? t("Entwurf", "Draft")).join(", ") };
    }).filter((r) => !!r.d);
  }, [b, data, lang]);
  const { sorted, sort, toggle } = useSorted(rows.filter((r) => matchesSearch(`${r.brand} ${r.d.title} ${monthOf(r.p.month, lang)} ${r.pill.label}`, search)), {
    monat: (r) => r.p.month,
    kooperation: (r) => `${r.brand} ${r.d.title}`,
    stand: (r) => r.pill.label,
    honorar: (r) => r.p.amount_cents,
    anteil: (r) => r.share,
    frist: (r) => r.due,
  }, { key: "monat" as SortKey, dir: -1 });

  if (base.error) return <ErrorState error={base.error} retry={() => void base.refetch()} />;
  if (base.isPending || !b || !data) return <Loading />;

  const visible = data.cards.filter((c) => (filter === "alle" || (filter === "aktiv" ? !c.done : c.done))
    && matchesSearch(`${c.title} ${c.deal.title} ${c.month ? monthOf(c.month, lang) : ""}`, search));
  const active = visible.filter((c) => !c.done);
  const done = visible.filter((c) => c.done);
  const archive = new Map<string, Card[]>();
  for (const c of done) {
    const k = c.month ?? "ohne Monat";
    archive.set(k, [...(archive.get(k) ?? []), c]);
  }
  const counts = { aktiv: data.cards.filter((c) => !c.done).length, erledigt: data.cards.filter((c) => c.done).length, alle: data.cards.length };

  function exportCsv() {
    const lines = [
      [t("Monat", "Month"), t("Marke", "Brand"), t("Kooperation", "Collaboration"), t("Modell", "Model"), t("Stand", "Status"), t("Währung", "Currency"),
        ...(budgetVisible ? [t("Budget netto", "Budget net")] : []),
        t("Dein Anteil netto", "Your net share"), t("Rechnung", "Invoice"), t("Content-Abgabe", "Content delivery"), t("Nutzungsrechte", "Usage rights"), t("Produkte pauschal versteuert", "Products taxed at a flat rate")],
      ...sorted.map((r) => [
        monthOf(r.p.month, lang), r.brand, r.d.title, modelLabel(r.d.model, lang), r.pill.label, r.d.currency, ...(budgetVisible ? [csvAmount(r.p.amount_cents)] : []),
        csvAmount(r.share), r.invoice, r.due ?? "", r.d.usage_rights ?? "", r.d.products_flat_taxed ? t("Ja", "Yes") : t("Nein", "No"),
      ]),
    ];
    downloadText(`${t("Kooperationen", "Collaborations")}_${today}.csv`, toCsv(lines));
  }

  const shareText = (c: Card) => {
    const o = c.period ? data.occ.get(c.period.id) : undefined;
    if (o?.shareCents != null) return t(`Dein Anteil: ${money(o.shareCents, c.deal.currency)}`, `Your share: ${money(o.shareCents, c.deal.currency)}`);
    const budget = c.period?.amount_cents ?? c.deal.budget_cents;
    // Ohne bekannten Anteil: Budget der Marke nur, wenn die Agentur es zeigt, und klar als Budget benannt (Befunde 20, 23)
    if (!budget) return t("Ohne Honorar", "No fee");
    return budgetVisible ? t(`Budget der Marke (vor Provision): ${money(budget, c.deal.currency)}`, `Brand budget (before commission): ${money(budget, c.deal.currency)}`)
      : t("Dein Anteil steht mit deiner Rechnung fest", "Your share is fixed with your invoice");
  };

  const invoiceFor = (c: Card) => (c.period ? b.invoices.filter((i) => i.period_id === c.period!.id && !["withdrawn", "discarded", "cancelled"].includes(i.status)) : []);
  const card = (c: Card) => {
    const periods = data.periodsByDeal.get(c.deal.id) ?? [];
    const due = monthDeadline(c.deal, c.period, periods.length, b.deliverables);
    const monthly = periods.length > 1;
    const doneCount = periods.filter((p) => monthDone(p, c.deal, b.invoices, today)).length;
    const own = invoiceFor(c);
    const fix = own.find((i) => i.status === "revision_requested" || i.status === "rejected");
    const draftInvoice = own.find((i) => i.status === "draft");
    const requested = !!c.period?.invoice_requested_at && own.every((i) => i.status === "draft");
    // Befund 1: geliefert, aber ohne Rechnung, auch ohne Anforderung
    const missing = !!c.period && !b.talent.self_billing_agreed_on && monthNeedsInvoice(c.period, c.deal, b.invoices, today);
    const facts = [modelLabel(c.deal.model, lang), c.deal.platforms?.map((x) => platformLabel(x, lang, b.platformNames)).join(", "), c.deal.deliverables?.trim(), c.deal.usage_rights?.trim(),
      isDirect(c.deal) ? t("Du rechnest direkt mit der Marke ab", "You bill the brand directly") : null].filter(Boolean);
    return (
      <section key={c.key} className="panel stack tk-cr-karte">
        <h3>{c.title}</h3>
        <div className="row between tk-cr-karte-kopf">
          <PillView pill={c.period ? monthPill(c.period, c.deal, b.invoices, lang, data.monthCtx) : dealStatusPill(c.deal.status, lang, b.statusLabels)} />
          <span className="num">{shareText(c)}</span>
        </div>
        {monthly && c.month && <small>{t(`${monthOf(c.month, lang)} · Monat ${periods.findIndex((p) => p.id === c.period?.id) + 1} von ${periods.length}`, `${monthOf(c.month, lang)} · month ${periods.findIndex((p) => p.id === c.period?.id) + 1} of ${periods.length}`)}</small>}
        {facts.length > 0 && <small>{facts.join(" · ")}</small>}
        <p className="tk-cr-frist">{t("Nächste Frist", "Next deadline")}: <strong>{due ? fmt(due) : t("keine", "none")}</strong></p>
        {c.deal.talent_note && <div className="revision-note">{c.deal.talent_note}</div>}
        {c.deal.status === "blocked" && (
          <p className="notice">{t("Deine Agentur hat diese Kooperation angehalten, etwa weil ein Vertrag, eine Freigabe oder eine Zahlung der Marke fehlt. Den Grund nennt sie dir im Chat; frag nach, wenn du ihn nicht kennst.",
            "Your agency has put this collaboration on hold, for example because a contract, an approval or a payment from the brand is missing. It tells you the reason in the chat; ask if you do not know it.")} <Link to="/creator/nachrichten">{t("Zum Chat", "Go to chat")}</Link></p>
        )}
        {missing && !fix && !draftInvoice && (
          <p className="notice">{c.period?.invoice_requested_at
            ? t("Deine Agentur hat die Rechnung für diesen Monat angefordert.", "Your agency has requested the invoice for this month.")
            : t("Dieser Monat ist geliefert, aber es gibt noch keine Rechnung von dir. Deine Agentur hat sie noch nicht angefordert; ohne Rechnung wird nicht ausgezahlt.", "This month is delivered but there is no invoice from you yet. Your agency has not requested it yet; without an invoice there is no payout.")}</p>
        )}
        {monthly && (
          <div className="stack tk-gap-sm">
            <div className="progress" role="img" aria-label={t(`${doneCount} von ${periods.length} Monaten erledigt`, `${doneCount} of ${periods.length} months done`)}>
              <span style={{ width: `${Math.round((doneCount / periods.length) * 100)}%` }}></span>
            </div>
            <small>{t(`${doneCount} von ${periods.length} Monaten erledigt`, `${doneCount} of ${periods.length} months done`)}</small>
          </div>
        )}
        {/* Paket 2: Fußzeile wie im Original, „Aktualisiert“ links, die Knöpfe rechts (bricht um, wenn der Platz fehlt) */}
        <div className="row tk-cr-karte-fuss">
          <small>{t(`Aktualisiert am ${fmt(c.deal.updated_at)}`, `Updated on ${fmt(c.deal.updated_at)}`)}</small>
          <Link className="btn " to={`/creator/deals/${c.deal.id}${c.month ? `?monat=${c.month}` : ""}`}>{t("Deal ansehen", "View deal")}</Link>
          {fix && <Link className="btn primary" to={`/creator/rechnungen/einreichen?rechnung=${fix.id}`}>{t("Rechnung korrigieren", "Correct invoice")}</Link>}
          {!fix && draftInvoice && <Link className="btn primary" to={`/creator/rechnungen/einreichen?rechnung=${draftInvoice.id}`}>{t("Entwurf fertigstellen", "Finish draft")}</Link>}
          {!fix && !draftInvoice && (requested || missing) && <Link className="btn primary" to={`/creator/rechnungen/einreichen?kooperation=${c.deal.id}&monat=${c.month}`}>{t("Rechnung einreichen", "Submit invoice")}</Link>}
        </div>
      </section>
    );
  };
  // TA-032: angeforderte bzw. gelieferte Monate ohne Rechnung (Befund 1) und Rechnungen mit Korrekturbedarf gleich oben
  const requestedCards = data.cards.filter((c) => !!c.period && ((c.period.invoice_requested_at && invoiceFor(c).length === 0)
    || (!b.talent.self_billing_agreed_on && monthNeedsInvoice(c.period, c.deal, b.invoices, today) && !invoiceFor(c).some((i) => i.status === "draft"))));
  const fixCards = data.cards.filter((c) => invoiceFor(c).some((i) => i.status === "revision_requested" || i.status === "rejected"));
  const flatTaxed = b.deals.some((d) => d.products_flat_taxed);

  return (
    <main id="main" className="content tk-cr tk-cr-deals">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Dein Creator-Portal", "Your creator portal")}</div>
          <h1>{t("Deine Deals", "Your deals")}</h1>
          <p>{t(`Kooperationen deiner Agentur ${b.org.name}, je Monat mit Stand, Anteil und Frist.`, `Collaborations of your agency ${b.org.name}, per month with status, share and deadline.`)}</p>
        </div>
        <div className="row">
          <button onClick={() => void base.refetch().then(() => toast(t("Kooperationen aktualisiert.", "Collaborations refreshed.")))} disabled={base.isFetching}>{t("Aktualisieren", "Refresh")}</button>
        </div>
      </div>
      {(requestedCards.length > 0 || fixCards.length > 0) && (
        <div className="integrity-banner">
          <p>
            <strong>
              {requestedCards.length > 0 && t(`Bitte erstelle deine Rechnung für ${requestedCards.map((c) => `${data.brand(c.deal)} · ${c.month ? monthOf(c.month, lang) : c.deal.title}`).join(", ")}`,
                `Please create your invoice for ${requestedCards.map((c) => `${data.brand(c.deal)} · ${c.month ? monthOf(c.month, lang) : c.deal.title}`).join(", ")}`)}
              {requestedCards.length > 0 && fixCards.length > 0 && " · "}
              {fixCards.length > 0 && `${t("Korrektur nötig", "Correction needed")}: ${fixCards.map((c) => `${data.brand(c.deal)} · ${c.month ? monthOf(c.month, lang) : c.deal.title}`).join(", ")}`}
            </strong>
          </p>
          {requestedCards[0] ? (
            <Link className="btn" to={`/creator/rechnungen/einreichen?kooperation=${requestedCards[0].deal.id}&monat=${requestedCards[0].month}`}>{t("Rechnung einreichen", "Submit invoice")}</Link>
          ) : (
            <Link className="btn" to="/creator/rechnungen">{t("Zu deinen Rechnungen", "Go to your invoices")}</Link>
          )}
        </div>
      )}
      <Toolbar
        search={search}
        onSearch={setSearch}
        label={t("Kooperationen suchen", "Search collaborations")}
        placeholder={t("Marke, Kooperation oder Monat …", "Brand, collaboration or month …")}
        filters={[
          { key: "aktiv" as Filter, label: t("Offen", "Open"), count: counts.aktiv },
          { key: "erledigt" as Filter, label: t("Erledigt", "Done"), count: counts.erledigt },
          { key: "alle" as Filter, label: t("Alle", "All") },
        ]}
        filter={filter}
        onFilter={setFilter}
      />
      {/* Befund 16: „aktiv“ überall gleich; hier stehen Monate, die Übersicht zählt Kooperationen */}
      <p className="muted">{t(`${data.active.size === 1 ? "1 aktive Kooperation" : `${data.active.size} aktive Kooperationen`} mit ${counts.aktiv === 1 ? "1 offenen Monat" : `${counts.aktiv} offenen Monaten`}. Ein Monat ist erledigt, wenn er ausgezahlt bzw. gebucht ist oder ohne Honorar abgeschlossen wurde.`,
        `${data.active.size === 1 ? "1 active collaboration" : `${data.active.size} active collaborations`} with ${counts.aktiv === 1 ? "1 open month" : `${counts.aktiv} open months`}. A month is done once it has been paid out or booked, or completed without a fee.`)}</p>
      {/* D-1 (Inhaber 21.10.2026): offene Monate ab breiter Ansicht als Raster aus zwei Karten nebeneinander */}
      {active.length > 0 && <div className="stack tk-cr-kartenraster" data-coco="deals-liste">{active.map(card)}</div>}
      {filter !== "erledigt" && active.length === 0 && (
        <div className="panel stack tk-empty">
          <h3>{search ? t("Keine passenden Ergebnisse.", "No matching results.") : t("Gerade läuft keine Kooperation.", "No collaboration is running right now.")}</h3>
          <p>{search ? t("Passe die Suche oder die Filter an.", "Adjust the search or filters.") : t("Neue Kooperationen deiner Agentur erscheinen hier automatisch.", "New collaborations from your agency appear here automatically.")}</p>
        </div>
      )}
      {filter !== "aktiv" && done.length > 0 && (
        <section className="stack">
          <div className="group-label">{t("Archiv", "Archive")}</div>
          {[...archive.entries()].sort((a, c) => c[0].localeCompare(a[0])).map(([month, list]) => (
            <section key={month} className="panel stack">
              <h3>{month === "ohne Monat" ? t("ohne Monat", "no month") : monthOf(month, lang)}</h3>
              {list.map((c) => (
                <Link key={c.key} className="task" to={`/creator/deals/${c.deal.id}${c.month ? `?monat=${c.month}` : ""}`}>
                  <div>
                    <strong>{c.deal.title}</strong>
                    <p>{data.brand(c.deal)} · {modelLabel(c.deal.model, lang)}</p>
                  </div>
                  <span className="num">{shareText(c)}</span>
                </Link>
              ))}
            </section>
          ))}
        </section>
      )}

      <section className="stack">
        <div className="row between">
          <div className="group-label">{t("Alle Monate", "All months")}</div>
          <button onClick={exportCsv} disabled={sorted.length === 0}>{t("Als CSV exportieren", "Export as CSV")}</button>
        </div>
        <div className="tablewrap tk-cardtable tk-cardwide tk-cr-liste">
          <table>
            <caption className="sr-only">{t("Alle Monate deiner Kooperationen", "All months of your collaborations")}</caption>
            <thead>
              <tr>
                <SortTh label={t("Monat", "Month")} k="monat" sort={sort} toggle={toggle} />
                <SortTh label={t("Kooperation", "Collaboration")} k="kooperation" sort={sort} toggle={toggle} />
                <SortTh label={t("Stand", "Status")} k="stand" sort={sort} toggle={toggle} />
                <SortTh label={t("Content-Abgabe", "Content delivery")} k="frist" sort={sort} toggle={toggle} />
                {budgetVisible && <SortTh label={t("Budget netto", "Budget net")} k="honorar" sort={sort} toggle={toggle} money />}
                <SortTh label={t("Dein Anteil", "Your share")} k="anteil" sort={sort} toggle={toggle} money />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.p.id} className="tk-cr-zeile" onClick={(e) => {
                  // D-1: die ganze Zeile öffnet den Monat der Kooperation; Links behalten ihre eigene Wirkung
                  if ((e.target as HTMLElement).closest("a")) return;
                  navigate(`/creator/deals/${r.d.id}?monat=${r.p.month.slice(0, 7)}`);
                }}>
                  <td data-label={t("Monat", "Month")}><Link to={`/creator/deals/${r.d.id}?monat=${r.p.month.slice(0, 7)}`}>{monthOf(r.p.month, lang)}</Link></td>
                  <td data-label={t("Kooperation", "Collaboration")}>
                    <span className="tk-cr-titel" title={`${r.brand} · ${r.d.title}`}>{r.brand} · {r.d.title}</span>
                    <small>{modelLabel(r.d.model, lang)}{r.d.platforms?.length ? ` · ${r.d.platforms.map((x) => platformLabel(x, lang, b.platformNames)).join(", ")}` : ""}{r.d.products_flat_taxed ? t(" · Produkte pauschal versteuert*", " · products taxed at a flat rate*") : ""}</small>
                  </td>
                  <td data-label={t("Stand", "Status")}><PillView pill={r.pill} />{r.invoice && <small>{r.invoice}</small>}</td>
                  <td data-label={t("Content-Abgabe", "Content delivery")}>{fmt(r.due)}</td>
                  {budgetVisible && <td data-label={t("Budget netto", "Budget net")}><span className="num">{money(r.p.amount_cents, r.d.currency)}</span></td>}
                  <td data-label={t("Dein Anteil", "Your share")}><span className="num">{r.share == null ? "–" : money(r.share, r.d.currency)}</span></td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={budgetVisible ? 6 : 5}>{search ? t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or filters.") : t("Noch keine Monate.", "No months yet.")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="source-note">
          {budgetVisible
            ? t("„Budget netto“ ist das Honorar der Marke vor Provision; „Dein Anteil“ ist dein Betrag nach der Provision deiner Agentur, netto. Liegt deine Rechnung vor, zählt ihr Nettobetrag.", "“Budget net” is the brand's fee before commission; “Your share” is your amount after your agency's commission, net. Once your invoice exists, its net amount counts.")
            : t("„Dein Anteil“ ist dein Betrag nach der Provision deiner Agentur, netto; liegt deine Rechnung vor, zählt ihr Nettobetrag.", "“Your share” is your amount after your agency's commission, net; once your invoice exists, its net amount counts.")}
          {flatTaxed ? ` * ${flatTaxNote(lang, b.org.country ?? null)}` : ""}
        </p>
      </section>
      <Buchungen bookings={b.bookings} brand={(id) => (id ? b.brands.get(id)?.name ?? null : null)} />
      <DemoNote />
    </main>
  );
}

const BOOKING_KIND: Record<string, [string, string]> = {
  bonus: ["Bonus", "Bonus"], usage_rights: ["Nutzungsrechte", "Usage rights"], performance: ["Erfolgsprämie", "Performance bonus"],
  expense: ["Auslagen", "Expenses"], correction: ["Korrektur", "Correction"], commission: ["Honorar", "Fee"], other: ["Weitere", "Other"],
};

/** Buchungen, die deine Agentur für dich sichtbar gemacht hat (Bonus, Nutzungsrechte …): eigene Zeilen, damit die Summen der Übersicht nachvollziehbar sind (Befund 16). */
function Buchungen({ bookings, brand }: { bookings: readonly CBooking[]; brand: (id: string | null) => string | null }) {
  const t = useT();
  const { lang } = useCreatorContext();
  if (!bookings.length) return null;
  const kind = (k: string) => {
    const l = BOOKING_KIND[k] ?? BOOKING_KIND.other!;
    return lang === "en" ? l[1] : l[0];
  };
  return (
    <section className="panel stack" aria-labelledby="buchungen">
      <h2 id="buchungen">{t("Weitere Beträge", "Other amounts")}</h2>
      <p>{t("Beträge außerhalb der Monate, die deine Agentur für dich gebucht hat, zum Beispiel Bonus oder verlängerte Nutzungsrechte. Sie zählen in der Übersicht mit.",
        "Amounts outside the months that your agency has booked for you, for example a bonus or extended usage rights. They count in the overview.")}</p>
      <ul className="tk-money-list">
        {[...bookings].sort((a, c) => c.booked_on.localeCompare(a.booked_on)).map((x) => (
          <li key={x.id}>
            <span>{kind(x.kind)}{brand(x.brand_id) ? ` · ${brand(x.brand_id)}` : ""} · {monthOf(x.booked_on.slice(0, 7), lang)}</span>
            <span className="num">{x.talent_share_cents == null ? "–" : money(x.talent_share_cents, x.currency)}</span>
            <small>{x.status === "booked" ? t("gebucht, dein Anteil netto", "booked, your share net") : t("geplant, dein Anteil netto", "planned, your share net")}</small>
          </li>
        ))}
      </ul>
    </section>
  );
}
